import { app, authentication } from "@microsoft/teams-js";
import { useEffect, useRef, useState } from "react";
import { v4 as newUuid } from "uuid";
import {
    actionlabelDocument,
    actionlabelTeamsDocumentId,
    actionRawJson,
    DYNIZER_TENANT_HEADER_KEY,
    RagMode,
} from "../models/const";
import { actionLabelTeamGroupId, actionLabelUserId } from "../models/pipeline";
import { Chat, Api as RagApi } from "../services/openapi/rag-axios/RagApi";
import { downloadObject } from "./Tab";

type Props = {
    tenant: string;
    fileIDs: string[];
    context: app.Context | undefined;
    ragMode: RagMode;
    openDriveItem: (itemID: string) => Promise<void>;
    setError: (err: string) => void;
};

const Mag = ({
    tenant,
    fileIDs,
    context,
    ragMode,
    openDriveItem,
    setError,
}: Props) => {
    const webcomponent = useRef<any>(null);
    const [uuid, setUuid] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setUuid(newUuid());

        webcomponent.current.data = [];
        webcomponent.current.config = {
            summary_pct: 50,
            max_tokens: 2500,
            temperature: 0.5,
            frequency_penalty: 0.5,
            top_p: 0.5,
            top_k: 10,
        };
    }, []);

    useEffect(() => {
        const currentRef = webcomponent.current;

        const handleSend = async (event: any) => {
            if (loading) return;

            if (!event?.detail?.text) return;

            setLoading(true);

            let conversationWithUserMessage = [
                ...webcomponent.current.data,
                {
                    message: event.detail.text,
                    role: "USER",
                },
            ];

            let requestBody: Chat = {
                text: event.detail.text,
                frequency_penalty: event.detail.frequency_penalty,
                max_tokens: event.detail.max_tokens,
                summary_pct: event.detail.summary_pct,
                temperature: event.detail.temperature,
                top_k: event.detail.top_k,
                top_p: event.detail.top_p,
                conversation_id: uuid,
                api_url: `https://${tenant}.dynizer.com:55556/api/v2`,
                rag_type: event.detail.dynizerAnnotation ? "ANNOTATE" : "TEXT",
                rag_mode: ragMode,
                document_selector: {
                    action: actionRawJson,
                    action_label: actionlabelDocument,
                    action_label_id: actionlabelTeamsDocumentId,
                    operation: "OR",
                    text_search: ragMode !== undefined,
                    vector_search: ragMode == "VECTOR",
                    filter: fileIDs.length
                        ? fileIDs.map((id) => ({
                              action_label: actionlabelTeamsDocumentId,
                              value: id,
                          }))
                        : context?.team?.groupId
                        ? [
                              {
                                  action_label: actionLabelTeamGroupId,
                                  value: context?.team?.groupId,
                              },
                          ]
                        : [
                              {
                                  action_label: actionLabelUserId,
                                  value: context?.user?.id,
                              },
                          ],
                },
            };

            try {
                let dynizerJwtToken = await authentication.getAuthToken();
                let response = await ragApi.cohere.chat(requestBody, {
                    headers: {
                        Authorization: `Bearer ${dynizerJwtToken}`,
                        [DYNIZER_TENANT_HEADER_KEY]: tenant,
                    },
                });
                if (webcomponent.current) {
                    webcomponent.current.data = response.data.conversation;
                    webcomponent.current.citations = response.data.citations;
                    webcomponent.current.citationDocuments =
                        response.data.citation_documents;
                }
            } catch {
                webcomponent.current.data = [
                    ...conversationWithUserMessage,
                    {
                        message:
                            "Something went wrong when sending the message",
                        role: "CHATBOT",
                        tool_calls: null,
                    },
                ];
            }

            setLoading(false);
        };

        const handleGetSelectedDocument = async (event: any) => {
            let documentID = event?.detail;

            webcomponent.current.documentInsightsLoading = true;
            try {
                let response = await downloadObject(
                    "document",
                    documentID,
                    tenant
                );
                if (webcomponent.current) {
                    webcomponent.current.documentInsights =
                        JSON.parse(response).document;
                }
            } catch (err: any) {
                setError(err);
            } finally {
                webcomponent.current.documentInsightsLoading = false;
            }
        };

        const handleDownloadSelectedDocument = async (event: any) => {
            let itemID = event?.detail;

            try {
                await openDriveItem(itemID);
            } catch (err: any) {
                setError(err);
            }
        };

        currentRef.addEventListener("submit", handleSend);
        currentRef.addEventListener(
            "getSelectedDocument",
            handleGetSelectedDocument
        );
        currentRef.addEventListener(
            "downloadSelectedDocument",
            handleDownloadSelectedDocument
        );

        return () => {
            currentRef.removeEventListener("submit", handleSend);
            currentRef.removeEventListener(
                "getSelectedDocument",
                handleGetSelectedDocument
            );
            currentRef.removeEventListener(
                "downloadSelectedDocument",
                handleDownloadSelectedDocument
            );
        };
    }, [loading, tenant, fileIDs, uuid]);

    const ragApi = new RagApi({
        baseURL: "/mag-api",
        headers: {
            "Content-Type": "application/json;charset=UTF-8",
        },
    });

    return <consono-rag ref={webcomponent}></consono-rag>;
};

export default Mag;
